import * as React from 'react'
import { Input } from 'reactstrap'
import { RasaReactComponent } from '../../../generic/rasaReactComponent'
import { EditSectionProps } from '../components'
import { EmailAttributes, getAttribute, MAX_COUNT_BELOW_ARTICLE_MODULE_COUNT_MESSAGE } from '../constants'
import { Dataset } from '../../../generic/dataset'
import { ToggleOff } from '../../../components/icons/toggleoff'
import { ToggleOn } from '../../../components/icons/toggleon'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import './styles.scss'
import { isNil } from 'lodash'

interface NewsLetterSettingState {
  alertMessage: {
    minCount: string,
  },
  validate: any,
  sections: any[],
  sectionMinCounts: { [key: number]: number },
  communityId: string,
  fullySectionedNewsletter: boolean,
}

export class NewsLetterSetting extends RasaReactComponent<EditSectionProps, NewsLetterSettingState> {
  private sharedStore: SharedStore

  constructor(props: EditSectionProps) {
    super(props, 'countsEdit', {
      alertMessage: {
        minCount: '',
      },
      validate: {
        [EmailAttributes.minArticles]: 'valid',
        [EmailAttributes.maxArticles]: 'valid',
        [EmailAttributes.descriptionLength]: 'valid',
        [EmailAttributes.maxArticlesPerSource]: 'valid',
      },
      sections: [],
      sectionMinCounts: {},
      communityId: null,
      fullySectionedNewsletter: getAttribute(props.data, EmailAttributes.fullySectionedNewsletter, false),
    })
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
    ]).then(([activeCommunity]) => {
      this.setState({ 
        communityId: activeCommunity.communityId,
        fullySectionedNewsletter: getAttribute(this.props.data, EmailAttributes.fullySectionedNewsletter, false)
      }, () => {
        this.loadSections()
      })
    })
  }

  private loadSections = () => {
    if (!this.state.communityId) return

    return new Dataset()
      .loadCommunityDataset('sections', this.state.communityId)
      .then((response) => {
        const sections = response[0] || []
        this.setState({ sections })
        // Load existing max counts from section options
        const sectionMinCounts = {}
        sections.forEach(section => {
          if (section.options) {
            const options = JSON.parse(section.options)
            if (!isNil(options.min_article_count)) {
              sectionMinCounts[section.category_type_id] = options.min_article_count
            }
          }
        })
        this.setState({ sectionMinCounts })
      })
  }

  private getSectionMinCount = (sectionId: number): number => {
    return this.state.sectionMinCounts[sectionId] || 0
  }

  private updateSectionMinCount = (sectionId: number, minCount: number) => {
    this.setState(prevState => ({
      sectionMinCounts: {
        ...prevState.sectionMinCounts,
        [sectionId]: minCount
      }
    }), () => {
      this.props.setSectionValid(this.props.name, true)
    })
  }

  private hasSectionChanges = () => {
    if (!this.state.sections || this.state.sections.length === 0) return false
    
    return this.state.sections.some(section => {
      const currentMinCount = this.state.sectionMinCounts[section.category_type_id]
      if (currentMinCount === undefined) return false
      
      try {
        const options = section.options ? JSON.parse(section.options) : {}
        return options.min_article_count !== currentMinCount
      } catch (e) {
        return true
      }
    })
  }

  private saveSectionSettings = () => {
    if (!this.state.sections || this.state.sections.length === 0 || !this.hasSectionChanges()) {
      return Promise.resolve()
    }

    const sections = this.state.sections
    const savePromises = sections.map(section => {
      const minCount = this.state.sectionMinCounts[section.category_type_id]
      if (minCount !== undefined) {
        return this.context.entityMetadata.getEntityObject('section')
          .then(entityObject => {
            entityObject.setRecordId(this.state.communityId, section.id)
            let options = section.options ? JSON.parse(section.options) : {}
            options = { 
              ...options,
              min_article_count: minCount 
            }
            entityObject.data.options = JSON.stringify(options)
            return entityObject.save()
          })
      }
      return Promise.resolve()
    })

    return Promise.all(savePromises)
  }

  public render() {
    return <div className="counts-editor">
      <span className="title">Article Counts</span>
      <div className="section mb-4">
        <div className="block">
          <div className="title">
            Maximum article count
          </div>
          <div className="description">
            What is the maximum number of articles that you want to display?
          </div>
          <Input value={getAttribute(this.props.data, EmailAttributes.maxArticles, 6)}
                 min={getAttribute(this.props.data, EmailAttributes.minArticles, 3)}
                 max={15}
                 type="number"
                 name={EmailAttributes.maxArticles}
                 valid={this.state.validate[EmailAttributes.maxArticles] === 'valid'}
                 invalid={this.state.validate[EmailAttributes.maxArticles] === 'invalid'}
                 onChange={(e) => {
                   this.validateInput(e)
                   this.setCount(EmailAttributes.maxArticles, e.target.valueAsNumber)
                 }}/>
          <div className="alert-message">
            {this.state.alertMessage.minCount}
          </div>
        </div>
        <div className="block">
          <div className="title article-count">
            Minimum article count
          </div>
          <div className="description">
            How many articles must be available for a newsletter to be sent?
          </div>
          <Input value={getAttribute(this.props.data, EmailAttributes.minArticles, 4)}
                 min={2}
                 max={getAttribute(this.props.data, EmailAttributes.maxArticles, 6)}
                 type="number"
                 name={EmailAttributes.minArticles}
                 valid={this.state.validate[EmailAttributes.minArticles] === 'valid'}
                 invalid={this.state.validate[EmailAttributes.minArticles] === 'invalid'}
                 onChange={(e) => {
                   this.validateInput(e)
                   this.setCount(EmailAttributes.minArticles, e.target.valueAsNumber)
                 }}/>
        </div>
        {this.state.sections && this.state.sections.length > 0 && (
          <div className="block">
            <div className="block-content">
              <div>
                <div className="title">
                  Fully Sectioned Newsletter
                </div>
                <div className="description">
                  Enable if you have no "non sectioned" content in the newsletter
                </div>
              </div>
              <div className="toggle-active" onClick={() =>
                this.props.onChange(EmailAttributes.fullySectionedNewsletter, 
                  !getAttribute(this.props.data, EmailAttributes.fullySectionedNewsletter, false))}>
                <button className="toggle-button"
                        style={{background: 'none', border: 'none', outline: 'none'}}>
                  {getAttribute(this.props.data, EmailAttributes.fullySectionedNewsletter, false) ? 
                    <ToggleOn/> : <ToggleOff/>}
                </button>
              </div>
            </div>
          </div>
        )}

        {getAttribute(this.props.data, EmailAttributes.fullySectionedNewsletter, false) && this.state.sections && this.state.sections.length > 0 && (
          <div className="block section-min-counts-block">
            <div className="title">
              Section Minimum Counts
            </div>
            <div className="description">
              Set minimum number of articles required per section
            </div>
            {this.state.sections.map((section) => (
              <div key={section.category_type_id} className="section-min-count">
                <div className="section-name">{section.name}</div>
                <Input type="number"
                       min={0}
                       max={getAttribute(this.props.data, EmailAttributes.maxArticles, 6)}
                       value={this.getSectionMinCount(section.category_type_id)}
                       onChange={(e) => this.updateSectionMinCount(section.category_type_id, e.target.valueAsNumber)}/>
              </div>
            ))}
          </div>
        )}
        <div className="block">
          <div className="title article-count">
            Maximum articles per source
          </div>
          <div className="description">
            How many articles per source do you want to display?
          </div>
          <Input value={getAttribute(this.props.data, EmailAttributes.maxArticlesPerSource, 4)}
                 min={1}
                 max={getAttribute(this.props.data, EmailAttributes.maxArticles, 6)}
                 type="number"
                 name={EmailAttributes.maxArticlesPerSource}
                 valid={this.state.validate[EmailAttributes.maxArticlesPerSource] === 'valid'}
                 invalid={this.state.validate[EmailAttributes.maxArticlesPerSource] === 'invalid'}
                 onChange={(e) => {
                   this.validateInput(e)
                   this.setCount(EmailAttributes.maxArticlesPerSource, e.target.valueAsNumber)
                 }}/>
        </div>
      </div>
      <div className="text-center">
        <button disabled={!this.props.isDirty} className="btn btn-secondary" onClick={() => {
          this.saveSectionSettings()
            .then(() => this.props.saveLayout())
        }}>Save
        </button>
      </div>
    </div>
  }

  private validateInput = (e: any) => {
    const {validate} = this.state
    const minValue = Number(e.target.min) || 2
    const maxValue = Number(e.target.max) || 6
    if (Number(e.target.value) >= minValue && Number(e.target.value) <= maxValue) {
      (validate[e.target.name] = 'valid')
    } else {
      (validate[e.target.name] = 'invalid')
    }
    this.setState({validate})
  }
  private setCount = (attribute: EmailAttributes, newValue: number) => {
    // Placeholder - in case we need to do cross-validation of other numbers
    this.props.onChange(attribute, newValue)
    if (this.state.validate[EmailAttributes.minArticles] === 'invalid' ||
      this.state.validate[EmailAttributes.maxArticles] === 'invalid' ||
      this.state.validate[EmailAttributes.descriptionLength] === 'invalid') {
      this.props.setSectionValid(this.props.name, false)
    } else {
      this.props.setSectionValid(this.props.name, true)
    }
    this.setAlertMessage(newValue)
  }

  private setAlertMessage = (newValue: number) => {
    const templateModules = this.props.data.template_modules
    const maxArticleModuleCount = templateModules.reduce((acc, cur) => {
      if (cur.type === 'article') {
        if (cur.moduleConfig.count && cur.moduleConfig.count > acc) {
          return cur.moduleConfig.count
        }
      }
      return acc
    }, 0)
    if (newValue < maxArticleModuleCount) {
      this.setState({alertMessage: {
          minCount: MAX_COUNT_BELOW_ARTICLE_MODULE_COUNT_MESSAGE,
        }})
    } else {
      this.setState({alertMessage: {
          minCount: '',
        }})
    }

  }
}
